/**
 * Zod schemas for Room action parameters.
 *
 * These schemas enable runtime validation of action payloads sent to a Room.
 * Each schema corresponds to a method on the Room interface.
 */
import { z } from 'zod'
import { entitySchemas } from './entity.schemas'

// ─────────────────────────────────────────────────────────────────────────────
// Mate Management Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const updateMatesArgsSchema = z.object({
  clientId: z.string(),
  mates: z.array(entitySchemas.mate),
})

const flushMatesArgsSchema = z.object({})

const deleteThumbnailFromHistoryArgsSchema = z.object({
  persistentId: z.string(),
  url: z.string(),
})

// ─────────────────────────────────────────────────────────────────────────────
// Game Lifecycle Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const startGameOptionsSchema = z.object({
  mateIDs: z.array(z.string()).optional(),
  mateGroups: z.array(z.array(z.string())).optional(),
  observerIDs: z.array(z.string()).optional(),
  randomizeSlotAssignment: z.boolean().optional(),
  gameOptions: z.record(z.string(), z.unknown()).optional(),
  challengeConfiguration: z.discriminatedUnion('challengeSource', [
    z.object({ challengeSource: z.literal('opponent') }),
    z.object({ challengeSource: z.literal('game'), gameChallengeArrangement: z.enum(['cooperative', 'sameChallengeRace', 'differentChallengeRace']) }),
  ]).optional(),
})

const startGameArgsSchema = z.object({
  gameId: z.string(),
  mateIDs: z.array(z.string()).optional(),
  mateGroups: z.array(z.array(z.string())).optional(),
  observerIDs: z.array(z.string()).optional(),
  randomizeSlotAssignment: z.boolean().optional(),
  gameOptions: z.record(z.string(), z.unknown()).optional(),
  challengeConfiguration: z.discriminatedUnion('challengeSource', [
    z.object({ challengeSource: z.literal('opponent') }),
    z.object({ challengeSource: z.literal('game'), gameChallengeArrangement: z.enum(['cooperative', 'sameChallengeRace', 'differentChallengeRace']) }),
  ]).optional(),
  /**
   * Release channel to pin this game to (default "stable"). A non-default
   * channel (e.g. "preview") puts the game in live-updating mode: the room
   * tracks new publishes to that channel and pushes them to all clients,
   * rather than pinning once at start.
   */
  channel: z.string().optional(),
})

const endGameArgsSchema = z.object({})

const makeMoveArgsSchema = z.object({
  move: z.unknown(),
  mateId: z.string().optional(),
  /** Hash of the state the submitting client believes precedes this move — used to detect replay divergence across clients. */
  stateHash: z.string().optional(),
  /** Submit as a host action rather than for the mate's player seat; only in a game started in managed mode. */
  asHost: z.boolean().optional(),
})

const forceGameStateArgsSchema = z.object({
  newState: z.object({
    G: z.unknown().optional(),
    ctx: z.unknown().optional(),
  }),
  /**
   * If provided, forceGameState is rejected unless the room's current
   * action-log length matches (basedOnSeqIndex + 1) — prevents a checkpoint
   * computed from a stale view from clobbering a move that arrived after it
   * was computed but before it was submitted. Omitted = no check (used by
   * the debug panel / test-state loading, which don't need it).
   */
  basedOnSeqIndex: z.number().optional(),
})

const resolvedGameVersionSchema = z.object({
  versionId: z.string(),
  clientBundleUrl: z.string(),
  styleUrl: z.string().optional(),
  metadata: z.object({
    minPlayers: z.number(),
    maxPlayers: z.number(),
    displayName: z.string().optional(),
    description: z.string().optional(),
    supportsSharedScreen: z.boolean().optional(),
    options: z.array(z.unknown()).optional(),
    playerIds: z.union([z.array(z.string()), z.record(z.string(), z.array(z.string()))]).optional(),
    challengeSources: z.array(z.enum(['opponent', 'game'])).optional(),
    gameChallengeArrangements: z.array(z.enum(['cooperative', 'sameChallengeRace', 'differentChallengeRace'])).optional(),
  }),
})

const setActiveGameVersionArgsSchema = z.object({
  gameId: z.string(),
  version: resolvedGameVersionSchema,
})

// ─────────────────────────────────────────────────────────────────────────────
// Game Setup Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const openGameArgsSchema = z.object({
  gameId: z.string(),
  clientId: z.string(),
  randomize: z.boolean().optional(),
})

const choosePlayerCountArgsSchema = z.object({
  count: z.number(),
})

const updateMateAssignmentArgsSchema = z.object({
  mateId: z.string(),
  slotIndex: z.number().nullable(),
  clientId: z.string(),
})

const startGameFromSetupArgsSchema = z.object({})

const cancelGameSetupArgsSchema = z.object({})

const getGameSetupArgsSchema = z.object({})

// ─────────────────────────────────────────────────────────────────────────────
// Game Options Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const getSavedGameOptionsArgsSchema = z.object({
  gameId: z.string(),
})

const getThumbnailHistoryArgsSchema = z.object({
  persistentId: z.string(),
})

const optionsScopeSchema = z.enum(['global', 'perPlayerCount'])

const setSavedGameOptionsArgsSchema = z.object({
  gameId: z.string(),
  scope: optionsScopeSchema,
  values: z.record(z.string(), z.unknown()),
  playerCount: z.number().optional(),
})

const restartGameArgsSchema = z.object({
  options: z
    .object({
      scope: optionsScopeSchema.optional(),
      values: z.record(z.string(), z.unknown()).optional(),
      playerCount: z.number().optional(),
    })
    .optional(),
})

// ─────────────────────────────────────────────────────────────────────────────
// Game Channel Preference Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const getGameChannelPreferenceArgsSchema = z.object({
  gameId: z.string(),
})

const setGameChannelPreferenceArgsSchema = z.object({
  gameId: z.string(),
  channel: z.string(),
})

const challengeConfigurationSchema = z.discriminatedUnion('challengeSource', [
  z.object({ challengeSource: z.literal('opponent') }),
  z.object({ challengeSource: z.literal('game'), gameChallengeArrangement: z.enum(['cooperative', 'sameChallengeRace', 'differentChallengeRace']) }),
])
const getChallengeConfigurationPreferenceArgsSchema = z.object({ gameId: z.string() })
const setChallengeConfigurationPreferenceArgsSchema = z.object({
  gameId: z.string(),
  challengeConfiguration: challengeConfigurationSchema,
})

// ─────────────────────────────────────────────────────────────────────────────
// Gameless Mode Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const setGamelessModeArgsSchema = z.object({
  gameless: z.boolean(),
})

// ─────────────────────────────────────────────────────────────────────────────
// Hosting Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const setHostArgsSchema = z.object({
  mateId: z.string(),
  isHost: z.boolean(),
})

const setManagedModeArgsSchema = z.object({
  managed: z.boolean(),
})

const setHostTermArgsSchema = z.object({
  /** What the room calls a host, such as "Teacher". */
  hostTerm: z.string().trim().min(1).max(24),
})

// ─────────────────────────────────────────────────────────────────────────────
// Curation Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const setCuratedModeArgsSchema = z.object({
  curated: z.boolean(),
})

const setCuratedGamesArgsSchema = z.object({
  /** The full ordered selection; a curated room offers a handful of games. */
  gameIds: z.array(z.string().min(1).max(200)).max(50),
})

// ─────────────────────────────────────────────────────────────────────────────
// State Action Schemas
// ─────────────────────────────────────────────────────────────────────────────

const getRoomStateArgsSchema = z.object({})

// ─────────────────────────────────────────────────────────────────────────────
// Exported Schema Object
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Action parameter schemas for runtime validation, keyed by action name.
 */
export const actionParameterSchemas = {
  // Mate management
  updateMates: updateMatesArgsSchema,
  flushMates: flushMatesArgsSchema,
  getThumbnailHistory: getThumbnailHistoryArgsSchema,
  deleteThumbnailFromHistory: deleteThumbnailFromHistoryArgsSchema,

  // Game lifecycle
  startGame: startGameArgsSchema,
  endGame: endGameArgsSchema,
  makeMove: makeMoveArgsSchema,
  forceGameState: forceGameStateArgsSchema,
  setActiveGameVersion: setActiveGameVersionArgsSchema,

  // Game setup
  openGame: openGameArgsSchema,
  choosePlayerCount: choosePlayerCountArgsSchema,
  updateMateAssignment: updateMateAssignmentArgsSchema,
  startGameFromSetup: startGameFromSetupArgsSchema,
  cancelGameSetup: cancelGameSetupArgsSchema,
  getGameSetup: getGameSetupArgsSchema,

  // Game options
  getSavedGameOptions: getSavedGameOptionsArgsSchema,
  setSavedGameOptions: setSavedGameOptionsArgsSchema,
  restartGame: restartGameArgsSchema,

  // Game channel preference
  getGameChannelPreference: getGameChannelPreferenceArgsSchema,
  setGameChannelPreference: setGameChannelPreferenceArgsSchema,
  getChallengeConfigurationPreference: getChallengeConfigurationPreferenceArgsSchema,
  setChallengeConfigurationPreference: setChallengeConfigurationPreferenceArgsSchema,

  // Gameless mode
  setGamelessMode: setGamelessModeArgsSchema,

  // Hosting
  setHost: setHostArgsSchema,
  setManagedMode: setManagedModeArgsSchema,
  setHostTerm: setHostTermArgsSchema,

  // Curation
  setCuratedMode: setCuratedModeArgsSchema,
  setCuratedGames: setCuratedGamesArgsSchema,

  // State
  getRoomState: getRoomStateArgsSchema,

  // Helper schemas
  startGameOptions: startGameOptionsSchema,
  optionsScope: optionsScopeSchema,
} as const

// ─────────────────────────────────────────────────────────────────────────────
// Inferred Types
// ─────────────────────────────────────────────────────────────────────────────

// Mate management
export type UpdateMatesArgs = z.infer<typeof updateMatesArgsSchema>
export type FlushMatesArgs = z.infer<typeof flushMatesArgsSchema>
export type GetThumbnailHistoryArgs = z.infer<typeof getThumbnailHistoryArgsSchema>
export type DeleteThumbnailFromHistoryArgs = z.infer<typeof deleteThumbnailFromHistoryArgsSchema>

// Game lifecycle
export type StartGameArgs = z.infer<typeof startGameArgsSchema>
export type StartGameOptions = z.infer<typeof startGameOptionsSchema>
export type EndGameArgs = z.infer<typeof endGameArgsSchema>
export type MakeMoveArgs = z.infer<typeof makeMoveArgsSchema>
export type ForceGameStateArgs = z.infer<typeof forceGameStateArgsSchema>
export type SetActiveGameVersionArgs = z.infer<typeof setActiveGameVersionArgsSchema>

// Game setup
export type OpenGameArgs = z.infer<typeof openGameArgsSchema>
export type ChoosePlayerCountArgs = z.infer<typeof choosePlayerCountArgsSchema>
export type UpdateMateAssignmentArgs = z.infer<typeof updateMateAssignmentArgsSchema>
export type StartGameFromSetupArgs = z.infer<typeof startGameFromSetupArgsSchema>
export type CancelGameSetupArgs = z.infer<typeof cancelGameSetupArgsSchema>
export type GetGameSetupArgs = z.infer<typeof getGameSetupArgsSchema>

// Game options
export type GetSavedGameOptionsArgs = z.infer<typeof getSavedGameOptionsArgsSchema>
export type SetSavedGameOptionsArgs = z.infer<typeof setSavedGameOptionsArgsSchema>
export type RestartGameArgs = z.infer<typeof restartGameArgsSchema>
export type OptionsScope = z.infer<typeof optionsScopeSchema>
export type GetGameChannelPreferenceArgs = z.infer<typeof getGameChannelPreferenceArgsSchema>
export type SetGameChannelPreferenceArgs = z.infer<typeof setGameChannelPreferenceArgsSchema>
export type GetChallengeConfigurationPreferenceArgs = z.infer<typeof getChallengeConfigurationPreferenceArgsSchema>
export type SetChallengeConfigurationPreferenceArgs = z.infer<typeof setChallengeConfigurationPreferenceArgsSchema>

// Gameless mode
export type SetGamelessModeArgs = z.infer<typeof setGamelessModeArgsSchema>

// State
export type GetRoomStateArgs = z.infer<typeof getRoomStateArgsSchema>

// Hosting
export type SetHostArgs = z.infer<typeof setHostArgsSchema>
export type SetManagedModeArgs = z.infer<typeof setManagedModeArgsSchema>
export type SetHostTermArgs = z.infer<typeof setHostTermArgsSchema>

// Curation
export type SetCuratedModeArgs = z.infer<typeof setCuratedModeArgsSchema>
export type SetCuratedGamesArgs = z.infer<typeof setCuratedGamesArgsSchema>
